import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Container, Row, Col } from "react-bootstrap";
import Select from "react-dropdown-select";
import moment from "moment";
import axios from "axios";
import { config } from "../../App";

const Loader = () => (
    <div className="loader-container">
        <img src="/images/loader.gif" alt="loading" />
    </div>
);

const imageUrlToBase64 = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png").split(",")[1]);
        };
        img.onerror = reject;
        img.src = url;
    });

const embedWatermarkInPdf = async (pdfBytes, watermarkBase64) => {
    if (!window.PDFLib) {
        await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    const { PDFDocument } = window.PDFLib;

    const pdfDoc = await PDFDocument.load(pdfBytes);
    const logoBytes = Uint8Array.from(atob(watermarkBase64), (c) => c.charCodeAt(0));
    const logoImage = await pdfDoc.embedPng(logoBytes);

    for (const page of pdfDoc.getPages()) {
        const { width, height } = page.getSize();
        const desiredWidth = width * 0.4;
        const scale = desiredWidth / logoImage.width;
        const imgWidth = logoImage.width * scale;
        const imgHeight = logoImage.height * scale;

        page.drawImage(logoImage, {
            x: (width - imgWidth) / 2,
            y: (height - imgHeight) / 2,
            width: imgWidth,
            height: imgHeight,
            opacity: 0.15,
        });
    }

    return pdfDoc.save();
};

const loadPdfJs = () =>
    new Promise((resolve, reject) => {
        if (window.pdfjsLib) return resolve(window.pdfjsLib);

        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
            resolve(window.pdfjsLib);
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });

const loadWatermarkImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const renderPdfWithWatermark = async (pdfBytes, containerEl, watermarkImg) => {
    const pdfjsLib = await loadPdfJs();

    const pdfData = new Uint8Array(pdfBytes);
    const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;

    containerEl.innerHTML = "";

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.5 });

        const pageWrapper = document.createElement("div");
        pageWrapper.style.cssText = `
            position: relative;
            margin: 0 auto 16px auto;
            width: ${viewport.width}px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            background: #fff;
        `;

        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.display = "block";

        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;

        if (watermarkImg) {
            const wDesiredWidth = viewport.width * 0.65;
            const wScale = wDesiredWidth / watermarkImg.naturalWidth;
            const wW = watermarkImg.naturalWidth * wScale;
            const wH = watermarkImg.naturalHeight * wScale;
            const wX = (viewport.width - wW) / 2;
            const wY = (viewport.height - wH) / 2;

            ctx.globalAlpha = 0.15;
            ctx.drawImage(watermarkImg, wX, wY, wW, wH);
            ctx.globalAlpha = 1.0;
        }

        pageWrapper.appendChild(canvas);
        containerEl.appendChild(pageWrapper);
    }
};

const ReimbursementSlip = () => {

    const token = sessionStorage.getItem("token");
    const userId = sessionStorage.getItem("userId");
    const doj = sessionStorage.getItem("doj");

    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [loading, setLoading] = useState(false);
    const [yearError, setYearError] = useState("");
    const [monthError, setMonthError] = useState("");
    const [success, setSuccess] = useState("");
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);

    const [pdfUrl, setPdfUrl] = useState(null);
    const [isSearched, setIsSearched] = useState(false);
    const [taxRegimeType, setTaxRegimeType] = useState("");
    const [rawPdfBytes, setRawPdfBytes] = useState(null);

    const pdfContainerRef = useRef(null);
    const watermarkImgRef = useRef(null);

    useEffect(() => {
        loadWatermarkImage("./images/logo-3.png")
            .then((img) => { watermarkImgRef.current = img; })
            .catch(() => { watermarkImgRef.current = null; });
    }, []);

    const getGetTaxRegimeType = async () => {
        try {
            const response = await axios.get(
                `${config.endpoint}/payroll/GetTaxRegimeType?employeeId=${userId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data;
            if (!data?.status) return;
            setTaxRegimeType(data.Data.TaxRegimeType);
        } catch (err) {
            console.error("fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const getCalendarData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${config.endpoint}/payroll/GetCalendarDetail?employeeId=${userId}&doj=${moment(doj, "DD-MMM-YYYY").format("DD-MM-YYYY")}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data;
            if (!data?.Status) return;

            const calendarData = data.CalendarData || [];

            const formattedYears = calendarData.map((y) => ({
                label: y.Year.toString(),
                value: y.Year,
                months: y.Months,
            }));

            setYears(formattedYears);

            const defaultYear = parseInt(data.DefaultYear);
            const defaultMonth = parseInt(data.DefaultMonth);

            const defaultYearObj = formattedYears.find((y) => y.value === defaultYear);

            if (defaultYearObj) {
                setSelectedYear(defaultYearObj);

                const formattedMonths = defaultYearObj.months.map((m) => ({
                    label: m.MonthName,
                    value: m.MonthValue,
                }));

                setMonths(formattedMonths);

                const defaultMonthObj = formattedMonths.find((m) => m.value === defaultMonth);
                setSelectedMonth(defaultMonthObj || null);
            }
        } catch (err) {
            console.error("fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (vals) => {
        const year = vals[0] || null;
        setSelectedYear(year);
        setYearError("");

        if (!year) {
            setMonths([]);
            setSelectedMonth(null);
            return;
        }

        const formattedMonths = year.months.map((m) => ({
            label: m.MonthName,
            value: m.MonthValue,
        }));

        setMonths(formattedMonths);

        if (selectedMonth && !formattedMonths.find((m) => m.value === selectedMonth.value)) {
            setSelectedMonth(null);
        }
    };

    useEffect(() => {
        getCalendarData();
        getGetTaxRegimeType();
    }, []);

    useEffect(() => {
        return () => {
            if (pdfUrl && pdfUrl !== "loaded") URL.revokeObjectURL(pdfUrl);
        };
    }, [pdfUrl]);

    const validateForm = () => {
        let isValid = true;
        setYearError("");
        setMonthError("");

        if (!selectedYear) {
            setYearError("Please select a Year");
            isValid = false;
        }

        if (!selectedMonth) {
            setMonthError("Please select a Month");
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSearched(true);
        setSuccess("");
        setRawPdfBytes(null);

        if (pdfUrl && pdfUrl !== "loaded") URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);

        if (pdfContainerRef.current) {
            pdfContainerRef.current.innerHTML = "";
        }

        if (!validateForm()) return;

        const payload = {
            employeeId: userId,
            Month: selectedMonth.value,
            Year: selectedYear.value,
            type: "reimbursement",
        };

        setLoading(true);

        try {
            const response = await axios.post(`${config.endpoint}/payroll/Getpayroll`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const data = response.data;
            if (!data?.Status) throw new Error(data?.Message || "API returned unsuccessful status");

            const base64String = data.Pdfbytes;
            if (!base64String || typeof base64String !== "string") throw new Error("No valid Pdfbytes received");

            const byteCharacters = atob(base64String);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }

            const downloadBuffer = byteArray.buffer.slice(0);
            const renderBuffer = byteArray.buffer.slice(0);

            setRawPdfBytes(downloadBuffer);
            setPdfUrl("loaded");

            setSuccess(
                `Reimbursement slip for ${selectedMonth.label} ${selectedYear.value} loaded successfully`
            );

            if (pdfContainerRef.current) {
                await renderPdfWithWatermark(
                    renderBuffer,
                    pdfContainerRef.current,
                    watermarkImgRef.current
                );
            }
        } catch (err) {
            console.error("Reimbursement slip fetch error:", err);
            setPdfUrl(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!rawPdfBytes) return;

        setLoading(true);
        try {
            const watermarkBase64 = await imageUrlToBase64("./images/logo-3.png");
            const watermarkedBytes = await embedWatermarkInPdf(rawPdfBytes, watermarkBase64);

            const blob = new Blob([watermarkedBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const fileName = `reimbursement_${selectedMonth?.label || "month"}_${selectedYear?.value || "year"}.pdf`;
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Watermark embed error:", err);
            alert("Failed to embed watermark. Downloading without watermark.");

            const blob = new Blob([rawPdfBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const fileName = `reimbursement_${selectedMonth?.label || "month"}_${selectedYear?.value || "year"}.pdf`;
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setLoading(false);
        }
    };

    const hasPdf = pdfUrl === "loaded" && rawPdfBytes;

    return (
        <>
            <section className="px-4">
                {loading && <Loader />}
                {loading && <div className="backdrop" />}
                <Container fluid>
                    <div className="card border-0 p-4 pb-2">
                        <h1 className="fs-22 fw-semibold mb-4">Reimbursement Slip</h1>
                        <Row>
                            {taxRegimeType !== "" && (
                                <Col lg={12} className="mb-4">
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #eef4ff 0%, #f5f0ff 100%)",
                                            border: "1px solid #c7d9f8",
                                            borderLeft: "5px solid #f97316",
                                            borderRadius: "8px",
                                            padding: "16px 20px",
                                            display: "flex",
                                            gap: "14px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <div style={{
                                            borderRadius: "50%",
                                            width: "36px",
                                            height: "36px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="#f97316"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="8" x2="12" y2="8" strokeWidth="3" />
                                                <line x1="12" y1="12" x2="12" y2="16" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="fw-semibold mb-1" style={{ color: "#1a3c6e", fontSize: "15px", marginTop: "7px" }}>
                                                You are under the <strong>{taxRegimeType}</strong>.
                                            </p>
                                            <p className="fw-semibold mb-1" style={{ color: "#f51811f6", fontSize: "14px", marginTop: "5px" }}>
                                                The September'2026 Reimbursement Slip is temporarily unavailable. It will be available shortly.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            )}

                            <Col lg={4} className="mb-4 pb-4">
                                <Form.Group controlId="year">
                                    <Form.Label className="mb-2">
                                        Year <sup className="primary-text font-md">*</sup>
                                    </Form.Label>
                                    <Select
                                        options={years}
                                        values={selectedYear ? [selectedYear] : []}
                                        onChange={handleYearChange}
                                        placeholder="Select Year"
                                        className="form-control shadow-none"
                                        dropdownHeight="240px"
                                        searchable
                                    />
                                    {yearError && (
                                        <div className="text-danger mt-1 small font-sm position-absolute">{yearError}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="mb-4 pb-4">
                                <Form.Group controlId="month">
                                    <Form.Label className="mb-2">
                                        Month <sup className="primary-text font-md">*</sup>
                                    </Form.Label>
                                    <Select
                                        options={months}
                                        values={selectedMonth ? [selectedMonth] : []}
                                        onChange={(vals) => {
                                            setSelectedMonth(vals[0] || null);
                                            setMonthError("");
                                        }}
                                        placeholder="Select Month"
                                        className="form-control"
                                        dropdownHeight="240px"
                                    />
                                    {monthError && (
                                        <div className="text-danger mt-1 small font-sm position-absolute">{monthError}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="mb-4 d-flex align-items-end pb-0 pb-xl-4 pb-lg-4">
                                <div className="d-flex gap-3">
                                    <Button type="submit" onClick={handleSubmit} className="btn btn-primary font-lg">Submit</Button>
                                    {hasPdf && (
                                        <Button onClick={handleDownload} type="button" className="btn btn-secondary font-lg">Download</Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="pdfViewSec mt-5 mb-5 card border-0 p-4">
                        <Row>
                            <Col lg={12}>
                                <div
                                    ref={pdfContainerRef}
                                    style={{
                                        maxHeight: "800px",
                                        overflowY: "auto",
                                        border: hasPdf ? "1px solid #ced4da" : "none",
                                        borderRadius: "6px",
                                        background: hasPdf ? "#f8f9fa" : "transparent",
                                        padding: hasPdf ? "16px" : "0",
                                    }}
                                />

                                {isSearched && !hasPdf && !loading && (
                                    <div className="card border-0 p-4 text-center text-muted font-md">
                                        <img
                                            src="/images/data-not-found.svg"
                                            className="img-fluid m-auto mt-5 mb-5 pt-5"
                                            alt="No data"
                                        />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Container>
            </section>
        </>
    );
};

export default ReimbursementSlip;